/**
 * P4-06: QueueItem —— 队列单项。
 *
 * 对齐 `js/jsx/queue.jsx.js:9-107`：
 * - 显示 label 与数量；
 * - [-] 移除 1 个，[x] 移除全部；
 * - [↓] 后移（非最后一项），[↑] 前移（非第一项）；
 * - 高亮库存不足项（`highlightUnavailable` + `isStorageLimited`）；
 * - tooltip 通过 `TooltipAnchor` 调用引擎 `ButtonModernHelper.getTooltipHTML`。
 */
import { TooltipAnchor } from "../../components/Tooltip/TooltipProvider";
import type { GamePage } from "../../bridge/GameBridge";

export interface QueueItemData {
  type: string;
  label: string;
  value: number;
  name: string;
}

interface QueueItemProps {
  item: QueueItemData;
  index: number;
  isLast: boolean;
  game: GamePage;
  queueManager: QueueManagerLike;
}

interface QueueManagerLike {
  remove(index: number, count: number): void;
  pushBack(index: number): void;
  pushFront(index: number): void;
  getQueueElementControllerAndModel(
    item: QueueItemData,
  ): { controller: unknown; model: unknown } | null;
  getQueueElementModel(item: QueueItemData): { prices?: { name: string; val: number }[] } | null;
}

export default function QueueItem({ item, index, isLast, game, queueManager }: QueueItemProps) {
  const isStorageLimited = () => {
    const model = queueManager.getQueueElementModel(item);
    if (!model || !model.prices) {
      return true;
    }
    return game.resPool.isStorageLimited(model.prices);
  };

  const limited = game.opts.highlightUnavailable && isStorageLimited();

  const getTooltip = () => {
    const controllerAndModel = queueManager.getQueueElementControllerAndModel(item);
    if (!controllerAndModel) {
      return "";
    }
    const helper = (window as { ButtonModernHelper?: { getTooltipHTML: (c: unknown, m: unknown) => string } })
      .ButtonModernHelper;
    if (!helper) {
      return "";
    }
    return helper.getTooltipHTML(controllerAndModel.controller, controllerAndModel.model);
  };

  return (
    <div className={limited ? "limited" : undefined}>
      {game.devMode ? `[${item.type}] - ` : null}
      <TooltipAnchor getTooltip={getTooltip} className="queue-label">
        {item.label}
      </TooltipAnchor>
      {item.value ? ` ${item.value}` : null}
      {" "}
      <a href="#" className="link" onClick={(e) => { e.preventDefault(); queueManager.remove(index, 1); }}>
        [-]
      </a>
      {item.value > 0 && (
        <a href="#" className="link" onClick={(e) => { e.preventDefault(); queueManager.remove(index, item.value); }}>
          [x]
        </a>
      )}
      {!isLast && (
        <a href="#" className="link" onClick={(e) => { e.preventDefault(); queueManager.pushBack(index); }}>
          [↓]
        </a>
      )}
      {index > 0 && (
        <a href="#" className="link" onClick={(e) => { e.preventDefault(); queueManager.pushFront(index); }}>
          [↑]
        </a>
      )}
    </div>
  );
}
